/**
 * Détection d'une date dans un nom de fichier audio/document.
 * Formats : 2026-07-15, 20260715, 15-07-2026, 260723 (AAMMJJ en tête de fichier).
 */

function isValidYmd(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day
  );
}

function toIso(year, month, day) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function hit(year, month, day) {
  if (!isValidYmd(year, month, day)) {
    return null;
  }
  return { date: toIso(year, month, day), month: toIso(year, month, 1).slice(0, 7), source: 'filename' };
}

function expandYear(yy) {
  const n = Number(yy);
  if (n >= 100) return n;
  return 2000 + n;
}

/**
 * @param {string} filename
 * @returns {{ date: string|null, month: string|null, source: 'filename'|null }}
 */
export function detectDateFromFilename(filename) {
  const base = String(filename || '').replace(/\.[^.]+$/, '');

  const iso = base.match(/(?<!\d)(\d{4})-(\d{2})-(\d{2})(?!\d)/);
  if (iso) {
    const found = hit(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    if (found) return found;
  }

  const compact = base.match(/(?<!\d)(\d{4})(\d{2})(\d{2})(?!\d)/);
  if (compact) {
    const found = hit(Number(compact[1]), Number(compact[2]), Number(compact[3]));
    if (found) return found;
  }

  const dmy = base.match(/(?<!\d)(\d{2})[-_.](\d{2})[-_.](\d{4})(?!\d)/);
  if (dmy) {
    const found = hit(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
    if (found) return found;
  }

  // AAMMJJ collé en tête de fichier : 260723 → 2026-07-23
  const prefixYyMmDd = base.match(/^(\d{2})(\d{2})(\d{2})(?!\d)/);
  if (prefixYyMmDd) {
    const found = hit(
      expandYear(prefixYyMmDd[1]),
      Number(prefixYyMmDd[2]),
      Number(prefixYyMmDd[3]),
    );
    if (found) return found;
  }

  const yyMmDd = base.match(/(?<!\d)(\d{2})(\d{2})(\d{2})(?!\d)/);
  if (yyMmDd) {
    const found = hit(expandYear(yyMmDd[1]), Number(yyMmDd[2]), Number(yyMmDd[3]));
    if (found) return found;
  }

  const yySep = base.match(/(?<!\d)(\d{2})[-_.](\d{2})[-_.](\d{2})(?!\d)/);
  if (yySep) {
    const a = Number(yySep[1]);
    const b = Number(yySep[2]);
    const c = Number(yySep[3]);
    const asYearFirst = hit(expandYear(a), b, c);
    if (asYearFirst) return asYearFirst;
    const asDayFirst = hit(expandYear(c), b, a);
    if (asDayFirst) return asDayFirst;
  }

  const ym = base.match(/(?<!\d)(\d{4})-(\d{2})(?!\d)/);
  if (ym) {
    const year = Number(ym[1]);
    const month = Number(ym[2]);
    if (month >= 1 && month <= 12) {
      return { date: toIso(year, month, 1), month: `${ym[1]}-${ym[2]}`, source: 'filename' };
    }
  }

  return { date: null, month: null, source: null };
}

/**
 * Objet document par défaut à partir du nom de fichier (sans extension).
 * @param {string} filename
 */
export function defaultObjetFromFilename(filename) {
  const base = String(filename || '').replace(/\.[^.]+$/, '').trim();
  const cleaned = base
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.slice(0, 255) || 'Document';
}
